import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  Chip,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
} from '@c2fo/liquidity';
import { CalendarDayIcon, EllipsisVerticalIcon, GemLightIcon, TrashCanIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import EmptyRender from '@/enterprise/components/EmptyRender';
import { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import useLocaleFormat from '@/utils/useLocaleFormat';
import usePricingTypeDisplay from '@/utils/usePricingTypeDisplay';
import BlendedRateDisplay from '../BlendedRateDisplay';
import EditInvoices from '../EditInvoices/EditInvoices';
import { EditInvoicesProvider } from '../EditInvoices/EditInvoicesContext';
import { useOfferBuilderContext } from '../OfferBuilderContext';
import RemoveItemModal, { RemoveItemModalProps } from '../RemoveItemModal';
import { useRateCalculations } from '../useRateCalculations';
import useStableGroupByBuyer from '../useStableGroupByBuyer';
import { buildTakerMarketKey, getWorkingTakerMarket } from '../utils';
import FundedByPartnerDisplay from './FundedByPartnerDisplay';
import TakerMarketRateDisplay from './TakerMarketRateDisplay';

interface CustomerReviewProps {
  offerSubmitting: boolean;
}

type RemoveItemModalState = RemoveItemModalProps extends infer U
  ? U extends unknown
    ? Omit<U, 'onClose'>
    : never
  : never;

const defaultRemoveItemModalState = {
  open: false,
  mode: undefined,
  buyer: undefined,
  takerMarket: undefined,
} satisfies RemoveItemModalState;

const CustomerReviewComponent = ({ offerSubmitting }: CustomerReviewProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { selectedCurrency } = useCurrencyContext();
  const {
    state: {
      frequency,
      workingTakerMarkets,
      reviewOnly,
      selected,
      builderVariant,
      draftOfferProperties,
      preferredRateSelected,
    },
  } = useOfferBuilderContext();
  const { getPricingTypeDisplayWithDetail } = usePricingTypeDisplay();
  const { asCurrency, asDateStringText, asNumber } = useLocaleFormat();
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedTakerMarket, setSelectedTakerMarket] = useState<TakerMarket | null>(null);
  const buyers = useStableGroupByBuyer({ takerMarkets: selected });
  const { calculateRateComparisons } = useRateCalculations();
  const rateComparisons = calculateRateComparisons();
  const [removeItemModalState, setRemoveItemModalState] = useState<RemoveItemModalState>(defaultRemoveItemModalState);
  const isDraftVariant = builderVariant === 'draft';

  const rateComparisonsByMarket = useMemo(
    () => new Map(rateComparisons.map((item) => [item.takerMarket, item])),
    [rateComparisons]
  );

  const handleCloseRemoveModal = () => {
    setRemoveItemModalState(defaultRemoveItemModalState);
  };

  const handleRemoveBuyer = (buyer: BuyerGroupWithStats) => {
    setRemoveItemModalState({
      open: true,
      mode: 'buyer',
      buyer,
    });
  };

  const handleRemoveTakerMarket = (takerMarket: TakerMarket) => {
    setRemoveItemModalState({
      open: true,
      mode: 'takerMarket',
      takerMarket,
    });
  };

  const closeInvoiceModal = () => {
    setShowInvoiceModal(false);
    setTimeout(() => {
      setSelectedTakerMarket(null);
    }, 300);
  };

  return (
    <>
      <RemoveItemModal {...removeItemModalState} onClose={handleCloseRemoveModal} />
      <EditInvoices
        key={`${selectedTakerMarket?.makerOrganizationUuid}-${selectedTakerMarket?.marketUuid}-${selectedTakerMarket?.takerDivisionId}`}
        onCloseInvoiceModal={closeInvoiceModal}
        readOnly
        referrer="offer-review"
        showInvoiceModal={showInvoiceModal}
        takerMarket={selectedTakerMarket}
      />
      <Accordion
        className="space-y-4"
        defaultValue={buyers.length === 1 ? [buyers[0].makerOrganizationUuid] : undefined}
        type="multiple"
      >
        {buyers.map((buyer) => {
          const rateChangedCount = buyer.takerMarkets.reduce((count, takerMarket) => {
            const comparison = rateComparisonsByMarket.get(takerMarket);
            return count + (comparison?.hasRateChanged ? 1 : 0);
          }, 0);

          return (
            <AccordionItem
              key={buyer.makerOrganizationUuid}
              className="rounded-xl border-[0.5px] border-stroke"
              value={buyer.makerOrganizationUuid}
            >
              <AccordionHeader asChild className="p-4">
                <div className="flex grow items-center gap-3">
                  {!reviewOnly && (
                    <div>
                      <IconButton
                        className="bg-transparent"
                        disabled={buyers.length === 1 || offerSubmitting}
                        icon={TrashCanIcon}
                        inverse
                        name="Remove buyer"
                        onClick={() => handleRemoveBuyer(buyer)}
                        size="sm"
                        variant="destructive"
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col gap-1 md:flex-row md:items-center md:gap-10">
                    <div>
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="font-bold">{buyer.makerOrganizationName}</div>
                          {!reviewOnly && rateChangedCount > 0 && (
                            <div>
                              <Chip
                                isRound
                                label={t('core.countUpdated', {
                                  formattedCount: asNumber({ value: rateChangedCount }),
                                })}
                                size="xs"
                                type="info"
                                variant="outlined"
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-text-secondary">
                          <BlendedRateDisplay takerMarkets={buyer.takerMarkets} />
                        </div>
                      </div>
                    </div>
                    <div className="md:ml-auto md:text-right">
                      <div className="text-lg font-bold">
                        {asCurrency(buyer.stats.eligibleInvoiceAmount, selectedCurrency)}
                      </div>
                      <div className="text-sm text-text-secondary">
                        {t('core.divisionWithCount', {
                          count: buyer.stats.totalDivisionCount,
                          formattedCount: asNumber({ value: buyer.stats.totalDivisionCount }),
                        })}
                      </div>
                      <div className="mt-0.5">
                        <FundedByPartnerDisplay buyer={buyer} />
                      </div>
                    </div>
                  </div>
                  <AccordionTrigger className="ml-0" name="Expand buyer" />
                </div>
              </AccordionHeader>
              <AccordionContent className="px-4">
                <div className="rounded-lg bg-canvas">
                  {buyer.takerMarkets.map((takerMarket) => (
                    <div key={takerMarket.id} className="flex gap-3 border-b-[0.5px] border-stroke p-4 last:border-b-0">
                      <div className="flex flex-1 flex-col gap-1 md:flex-row md:items-center md:gap-10">
                        <div className="text-sm font-medium">
                          <div>{getTakerMarketName(takerMarket)}</div>
                          <div className="text-sm" title={getTakerMarketDivisionTitle(takerMarket)?.title}>
                            {getTakerMarketDivisionTitle(takerMarket)?.content}
                          </div>
                          <div>
                            <TakerMarketRateDisplay takerMarket={takerMarket} />
                          </div>
                          <div className="flex items-center gap-1 text-sm font-normal text-text-secondary">
                            {(isDraftVariant && draftOfferProperties?.offerType === 'PREFERRED') ||
                            preferredRateSelected ? (
                              <>
                                <GemLightIcon className="size-5 fill-text-secondary" />
                                {t('core.preferredRate')}
                              </>
                            ) : (
                              getPricingTypeDisplayWithDetail(
                                takerMarket,
                                frequency!,
                                getWorkingTakerMarket(workingTakerMarkets, buildTakerMarketKey(takerMarket))
                              )
                            )}
                          </div>
                        </div>
                        <div className="space-y-1.5 md:ml-auto md:text-right">
                          <div>
                            <div className="font-medium">
                              {asCurrency(takerMarket.eligibleInvoiceAmount, takerMarket.currency)}
                            </div>
                            <div className="text-sm text-text-secondary">
                              {t('core.invoiceWithCount', {
                                formattedCount: asNumber({ value: takerMarket.eligibleInvoiceCount }),
                                count: takerMarket.eligibleInvoiceCount,
                              })}
                            </div>
                            <div className="text-sm text-text-secondary">
                              {`${asNumber({ value: takerMarket.eligibleDpeWeightedAvg })} ${t('core.daysPaidEarly')}`}
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5 text-sm text-text-secondary md:justify-end">
                            <CalendarDayIcon className="size-5 fill-text-secondary" />
                            <EmptyRender condition={!!takerMarket.marketGoodFundsDate}>
                              {takerMarket.marketGoodFundsDate &&
                                asDateStringText({ date: takerMarket.marketGoodFundsDate })}
                            </EmptyRender>
                          </div>
                          <div className="flex md:justify-end">
                            <FundedByPartnerDisplay takerMarket={takerMarket} />
                          </div>
                        </div>
                      </div>
                      <div className="shrink-0 md:ml-auto md:text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <IconButton
                              className="bg-transparent"
                              disabled={offerSubmitting}
                              icon={EllipsisVerticalIcon}
                              name="Actions"
                              size="sm"
                              variant="ancillary"
                            />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" avoidCollisions={false} collisionPadding={24}>
                            <DropdownMenuItem
                              onClick={() => {
                                trackEnterpriseEvent('invoices::view-opened', {
                                  makerOrgUuid: takerMarket.makerOrganizationUuid,
                                  marketUuid: takerMarket.marketUuid,
                                  takerUuid: takerMarket.takerDivisionUuid,
                                  referrer: 'offer-review',
                                  variant: builderVariant,
                                });

                                setSelectedTakerMarket(takerMarket);
                                setShowInvoiceModal(true);
                              }}
                            >
                              {t('core.viewInvoices')}
                            </DropdownMenuItem>
                            {!reviewOnly && (
                              <DropdownMenuItem
                                className="text-red-700"
                                onClick={() => handleRemoveTakerMarket(takerMarket)}
                                disabled={selected.length === 1}
                              >
                                {t('builder.removeItemModalButton')}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </>
  );
};

const CustomerReview = (props: CustomerReviewProps) => {
  return (
    <EditInvoicesProvider>
      <CustomerReviewComponent {...props} />
    </EditInvoicesProvider>
  );
};

export default CustomerReview;
